/**
 * App — the top-level shell component.
 *
 * Wires board loading state → BoardGrid. Handles all load states:
 * - loading: skeleton / splash
 * - ready: render the grid + edit mode toolbar + onboarding bar
 * - future-version: friendly message (never mangle a newer-build board)
 * - error: informative but not alarming (calm brand)
 *
 * The cache and registry come from the composition point (src/client/composition.ts),
 * which is the single place to swap mock → real client. The App itself is ignorant
 * of which implementation is in use.
 *
 * Edit mode: useEditableBoard manages board mutations; AddTileDrawer handles the
 * add-tile flow; EditModeBar provides the calm affordance entry point.
 *
 * Onboarding: OnboardingBar renders below the board (never above — value before
 * configuration). It emits template_selected and swaps the board in-place.
 *
 * Analytics: page_loaded is emitted once on board hydration (cold vs returning).
 * board_snapshot is emitted after board hydration (metrics-plan §1.5).
 * board_customized fires in useEditableBoard on the first self-directed op.
 *
 * Tile detail sheet: tapping a tile OUTSIDE edit mode opens TileDetailSheet.
 * Wires onConfigure through useEditableBoard.commitConfigure (D-011).
 *
 * Crossfade (core-flows §2.2): on template swap, tiles are classified into
 * entering/leaving/stable sets and receive CSS classes for a calm 200ms fade.
 * Stable tiles (same type in both boards) do not animate.
 * prefers-reduced-motion: no animation, instant swap.
 *
 * has_geo (metrics-plan §1.2): surfaced from board source — a persisted board
 * with lat/lon different from the SF fallback is evidence geo succeeded. A default
 * board seeded by geolocation writes the real coords; we infer from whether those
 * differ from SF_LAT/SF_LON (imperfect but the only signal available without
 * threading the result through buildDefaultBoard, which we cannot modify).
 */
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Minimal matchMedia hook — mirrors the one in SpacesSwitcher.tsx but scoped
 * here to avoid cross-file coupling. Both read the same breakpoint constant.
 * DESKTOP_BREAKPOINT is defined once; never duplicate the literal.
 */
const DESKTOP_BREAKPOINT_PX = 768;

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => { setMatches(e.matches); };
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => { mql.removeEventListener('change', handler); };
  }, [query]);
  return matches;
}
import { useSpacesState } from './board/use-spaces.js';
import { useEditableBoard, getCustomizationOps } from './board/use-editable-board.js';
import { BoardGrid } from './components/BoardGrid.js';
import { DragGhost, TargetCellOutline, ResizeOutline } from './components/DragGhost.js';
import { TileDetailSheet } from './components/TileDetailSheet.js';
import { EditModeBar } from './edit-mode/EditModeBar.js';
import { AddTileDrawer } from './add-tile/AddTileDrawer.js';
import { useDragMove } from './edit-mode/use-drag-move.js';
import { useDragResize } from './edit-mode/use-drag-resize.js';
import { useFlipAnimation, layoutSignature } from './edit-mode/use-flip-animation.js';
import { useKeyboardNav } from './edit-mode/use-keyboard-nav.js';
import { findAddPosition, hasCollision, computeDisplacements, pointerToGridX, pointerToGridY } from './edit-mode/layout-engine.js';
import { appCache, appRegistry } from '../client/composition.js';
import { appAnalytics } from './analytics/index.js';
import { buildEnvelope } from './analytics/identity.js';
import { OnboardingBar } from './onboarding/OnboardingBar.js';
import { classifyTilesForCrossfade } from './onboarding/stable-tiles.js';
import { SpacesSwitcher } from './spaces/SpacesSwitcher.js';
import { boardHash } from './analytics/board-hash.js';
import { isActivated } from './analytics/activation.js';
import { useDisplayMode } from './tv/use-display-mode.js';
import { useCursorHide } from './tv/use-cursor-hide.js';
import type { GlanceViewModel } from 'briefpane/contracts';
import type { Tile, Board, TileSize } from 'briefpane/schema';
import { getTileTypeDef, sizeToWH } from 'briefpane/schema';

// Grid cell dimensions (pixels) — must match CSS gridAutoRows + gap
const CELL_WIDTH = 132; // 120px cell + 12px gap
const CELL_HEIGHT = 132;

/** Per-tile crossfade role during a template swap (core-flows §2.2). */
export type TileCrossfadeRole = 'entering' | 'leaving' | 'stable';

/** Maps tileId → its crossfade role for the current swap animation. */
export type CrossfadeMap = ReadonlyMap<string, TileCrossfadeRole>;

/** Detect prefers-reduced-motion at the module level. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function AppShell(): React.ReactElement {
  const displayMode = useDisplayMode();
  const spacesState = useSpacesState(appAnalytics);
  // Lifted here (not just inside SpacesSwitcher) so ReadyBoard can condition
  // the tabpanel ARIA role on it — avoids dangling labelledby on phone/TV.
  const isDesktop = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`);
  const [dismissPersistBanner, setDismissPersistBanner] = useState(false);
  const [swappedBoard, setSwappedBoard] = useState<Board | null>(null);
  const [crossfadeMap, setCrossfadeMap] = useState<CrossfadeMap>(new Map());
  const crossfadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TV mode: parse optional ?space=<name-or-id> param to select a space.
  // Matched case-insensitively; unknown value → active space (no change).
  const isTV = displayMode === 'tv';
  useEffect(() => {
    if (!isTV || spacesState.status !== 'ready') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const spaceParam = params.get('space');
      if (!spaceParam) return;
      const lower = spaceParam.toLowerCase();
      const match = spacesState.spaces.find(
        (s) => s.id === spaceParam || s.name.toLowerCase() === lower,
      );
      if (match && match.id !== spacesState.activeSpace?.id) {
        // Pass null: TV URL resolution is automatic, not user-initiated.
        // The schema docstring (§1.S.2) explicitly says TV resolution does NOT fire space_switched.
        spacesState.actions.switchSpace(match.id, null);
      }
    } catch { /* ignore — exotic URL env */ }
  // Run once when spaces become ready (TV displays don't navigate).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTV, spacesState.status]);

  // Set data-display-mode on <html> once so CSS handles all the heavy lifting.
  useEffect(() => {
    document.documentElement.setAttribute('data-display-mode', displayMode);
    return () => {
      document.documentElement.removeAttribute('data-display-mode');
    };
  }, [displayMode]);

  useEffect(
    () => () => {
      if (crossfadeTimerRef.current !== null) clearTimeout(crossfadeTimerRef.current);
    },
    [],
  );

  const swappedBoardRef = useRef(swappedBoard);
  swappedBoardRef.current = swappedBoard;

  const handleBoardSwap = useCallback((newBoard: Board) => {
    const activeBoard = spacesState.activeSpace?.board ?? null;
    const oldBoard = swappedBoardRef.current ?? activeBoard;
    if (oldBoard && !prefersReducedMotion()) {
      const { entering, leaving, stableIncoming, stableOutgoing } = classifyTilesForCrossfade(oldBoard, newBoard);
      const map = new Map<string, TileCrossfadeRole>();
      for (const id of entering) map.set(id, 'entering');
      for (const id of leaving) map.set(id, 'leaving');
      for (const id of stableIncoming) map.set(id, 'stable');
      for (const id of stableOutgoing) map.set(id, 'stable');
      setCrossfadeMap(map);
      if (crossfadeTimerRef.current !== null) clearTimeout(crossfadeTimerRef.current);
      crossfadeTimerRef.current = setTimeout(() => {
        crossfadeTimerRef.current = null;
        setCrossfadeMap(new Map());
      }, 350);
    }
    setSwappedBoard(newBoard);
  }, [spacesState.activeSpace]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the active space changes (user switches spaces), clear any swapped board
  // so the new space's board shows instead of the old swap.
  const prevActiveSpaceIdRef = useRef<string | null>(null);
  useEffect(() => {
    const activeId = spacesState.activeSpace?.id ?? null;
    if (activeId !== null && activeId !== prevActiveSpaceIdRef.current) {
      prevActiveSpaceIdRef.current = activeId;
      setSwappedBoard(null);
      setCrossfadeMap(new Map());
    }
  }, [spacesState.activeSpace?.id]);

  if (spacesState.status === 'loading') {
    return (
      <main
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          color: 'var(--color-text-dim)',
          fontSize: 'var(--text-detail)',
        }}
        aria-busy="true"
        aria-label="Loading your board"
      >
        Loading your board…
      </main>
    );
  }

  if (spacesState.status === 'future-version') {
    return (
      <main
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 'var(--space-4)',
          padding: 'var(--space-4)',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: 'var(--text-detail)', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
          Board saved by a newer version
        </h1>
        <p style={{ fontSize: 'var(--text-delta)', color: 'var(--color-text-secondary)', margin: 0, maxWidth: '360px' }}>
          Your saved board was created by a newer version of Briefpane. Please
          clear your browser data or wait for the app to update.
        </p>
      </main>
    );
  }

  if (spacesState.status === 'error') {
    return (
      <main
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 'var(--space-4)',
          padding: 'var(--space-4)',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: 'var(--text-detail)', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
          Unavailable
        </h1>
        <p style={{ fontSize: 'var(--text-delta)', color: 'var(--color-text-secondary)', margin: 0, maxWidth: '360px' }}>
          {spacesState.errorDetail ?? 'An error occurred loading your board.'}
        </p>
      </main>
    );
  }

  // status === 'ready'
  if (!spacesState.activeSpace) {
    // Should not happen — useSpacesState always provides an active space when ready.
    return <></>;
  }

  // The board to display: a swapped board (template select) or the active space's board.
  const activeBoard = swappedBoard ?? spacesState.activeSpace.board;

  const showPersistBanner = spacesState.persistFailed && !dismissPersistBanner;

  return (
    <>
      {/* CRITICAL-2: calm storage-full banner — shown when last persist failed. */}
      {!isTV && showPersistBanner && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)',
            backgroundColor: 'var(--color-bg-input)',
            borderBottom: '1px solid var(--color-border)',
            fontSize: 'var(--text-detail)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <span>
            Couldn&apos;t save — storage may be full. Free up space and your next edit will retry.
          </span>
          <button
            type="button"
            aria-label="Dismiss storage warning"
            onClick={() => { setDismissPersistBanner(true); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-dim)',
              fontSize: 'var(--text-delta)',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
      <ReadyBoard
        // Key includes active space id: space switches must remount ReadyBoard so
        // useEditableBoard reinitializes with the new space's board. Template swaps
        // are also covered: template board ids are distinct from the space board id.
        // Combined key: `${spaceId}:${boardId}` — both space switch AND template
        // swap trigger a remount and a clean repaint.
        key={`${spacesState.activeSpace.id}:${activeBoard.id}`}
        initialBoard={activeBoard}
        onBoardSwap={handleBoardSwap}
        onBoardCommit={spacesState.actions.updateActiveBoard}
        crossfadeMap={crossfadeMap}
        displayMode={displayMode}
        spaces={spacesState.spaces}
        activeSpaceId={spacesState.activeSpace.id}
        spacesActions={spacesState.actions}
        isDesktop={isDesktop}
      />
    </>
  );
}

interface ReadyBoardProps {
  readonly initialBoard: Board;
  readonly onBoardSwap: (newBoard: Board) => void;
  /**
   * THE BRIDGE: called after every board-op commit so the spaces layer can fold
   * the new board into the active space and persist to SPACES_STORAGE_KEY.
   * Returns true if the active space was found and updated; false if it was not
   * (race: user switched spaces before the commit landed — HIGH-2).
   */
  readonly onBoardCommit: (newBoard: Board) => boolean;
  /** Per-tile crossfade classification for template swaps. Empty map = no animation. */
  readonly crossfadeMap: CrossfadeMap;
  /** Display mode threaded from AppShell — controls chrome visibility. */
  readonly displayMode: import('./tv/use-display-mode.js').DisplayMode;
  /** All spaces — passed to SpacesSwitcher and used for analytics enrichment. */
  readonly spaces: readonly import('briefpane/schema').Space[];
  /** Currently active space id. */
  readonly activeSpaceId: string;
  /** Spaces actions — passed to SpacesSwitcher. */
  readonly spacesActions: import('./board/use-spaces.js').SpacesActions;
  /**
   * Whether the viewport is ≥ DESKTOP_BREAKPOINT_PX (lifted from SpacesSwitcher
   * to this level so the board-panel ARIA role can be conditioned on it).
   * ARIA FIX: role=tabpanel + aria-labelledby must only be set when the desktop
   * tab strip (tablist) is actually rendered — on phone/TV those tab ids don't
   * exist, creating a dangling ARIA reference.
   */
  readonly isDesktop: boolean;
}

/**
 * Infer has_geo from the weather tile's params.
 *
 * A default board seeds weather with SF fallback (37.77, -122.42). If the loaded
 * board has a weather tile with coords that differ from that fallback, geolocation
 * succeeded. This is the only signal available without threading the geo result
 * through buildDefaultBoard (which we cannot modify per territory rules).
 *
 * Returns false for persisted boards (geo was resolved on a prior session) to
 * avoid a false positive when the persisted board happens to have real coords.
 */
const SF_LAT = 37.77;
const SF_LON = -122.42;
const SF_LAT_EPSILON = 0.01;
const SF_LON_EPSILON = 0.01;

function inferHasGeo(board: Board, boardSource: 'persisted' | 'default'): boolean {
  // Only meaningful for a freshly-seeded default board (not a persisted one).
  if (boardSource !== 'default') return false;
  const weatherTile = board.tiles.find((t) => t.type === 'weather.current');
  if (!weatherTile?.source) return false;
  const params = weatherTile.source.params as Record<string, unknown>;
  const lat = params.lat as number | undefined;
  const lon = params.lon as number | undefined;
  if (lat === undefined || lon === undefined) return false;
  // If coords are different from SF fallback → geo succeeded.
  return (
    Math.abs(lat - SF_LAT) > SF_LAT_EPSILON ||
    Math.abs(lon - SF_LON) > SF_LON_EPSILON
  );
}

function ReadyBoard({ initialBoard, onBoardSwap, onBoardCommit, crossfadeMap, displayMode, spaces, activeSpaceId, spacesActions, isDesktop }: ReadyBoardProps): React.ReactElement {
  const isTV = displayMode === 'tv';
  // TV mode: auto-hide cursor after 3s idle (spec §2.4).
  useCursorHide(isTV);

  const editable = useEditableBoard(initialBoard, appAnalytics, onBoardCommit);
  const [addTileOpen, setAddTileOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  // Pointer position for the drag ghost (relative to grid container).
  const [pointerPos, setPointerPos] = useState({ x: 0, y: 0 });
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
  // Live displacement preview state (SHOULD #9): tileId → pixel offsets.
  const [displacements, setDisplacements] = useState<ReadonlyMap<string, { dx: number; dy: number }>>(new Map());

  // Tile detail sheet state (level-1 disclosure — core-flows §4.1).
  const [detailTile, setDetailTile] = useState<Tile | null>(null);
  // Per-tile view model cache for the detail sheet. We store the last-rendered
  // vm for each tile id so the sheet shows live data without re-fetching.
  const [detailVm, setDetailVm] = useState<GlanceViewModel | null>(null);
  // Enriched data from the registry entry (e.g. humidity/wind for weather).
  const [detailData, setDetailData] = useState<Record<string, unknown> | undefined>(undefined);

  // Shared vm registry: TileCell writes its latest GlanceViewModel here after each
  // render so the detail sheet can show live data at the moment the user taps a tile.
  // Now stores { vm, data? } to support enriched detail data (Deliverable 6).
  // This is a mutable ref (not state) — writes do not cause re-renders.
  const vmRegistryRef = useRef<Map<string, { vm: GlanceViewModel; data?: Record<string, unknown> }>>(new Map());

  // H4: quantize displacement recomputes by cell — skip when hover cell is unchanged.
  const prevHoverCellRef = useRef<{ x: number; y: number } | null>(null);

  // Emit page_loaded + board_snapshot once on board hydration (§1.2, §1.5).
  // board_source is 'persisted' for all spaces-loaded boards (the distinction between
  // default and persisted was only meaningful for the legacy single-board path; spaces
  // always comes from the spaces slot or legacy migration, so 'persisted' is accurate).
  const hasGeo = useMemo(
    () => inferHasGeo(initialBoard, 'persisted'),
    [initialBoard],
  );

  useEffect(() => {
    if (!appAnalytics.enabled) return;
    try {
      const envelope = buildEnvelope();

      // page_loaded (§1.2) — board_source is always 'persisted' via the spaces loader.
      appAnalytics.emit({
        event: 'page_loaded',
        load_type: envelope.is_returning ? 'returning' : 'cold',
        board_source: 'persisted',
        tile_count: initialBoard.tiles.length,
        has_geo: hasGeo,
        ...envelope,
      });

      // board_snapshot (§1.5) — fires on load alongside page_loaded.
      // mutated_since_default is sticky: once true it stays true (§1.5).
      // §1.S enrichment: space_count + active_space_index measure Spaces adoption.
      const activeSpaceIdx = spaces.findIndex((s) => s.id === activeSpaceId);
      appAnalytics.emit({
        event: 'board_snapshot',
        tile_count: initialBoard.tiles.length,
        mutated_since_default: isActivated(),
        customization_ops: getCustomizationOps(),
        board_hash: boardHash(initialBoard),
        space_count: Math.max(1, spaces.length),
        active_space_index: Math.max(0, activeSpaceIdx),
        ...envelope,
      });
    } catch { /* swallow — analytics never blocks render */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fires once on mount

  // A stable ref to hold the FLIP capture function, set after drag/resize are
  // initialized. The commit callbacks below read from this ref at call time so
  // they always invoke the latest capture function regardless of definition order.
  const flipCaptureRef = useRef<((excludeTileId?: string) => void) | null>(null);

  // Drag-to-move — capture FLIP rects before the commit mutates board state.
  // The dragged tile id is EXCLUDED from the replay (its ghost was the live
  // feedback; it snaps into place) — displaced neighbors still glide.
  const drag = useDragMove(
    editable.board,
    gridRef,
    CELL_WIDTH,
    CELL_HEIGHT,
    (tileId, x, y) => {
      flipCaptureRef.current?.(tileId);
      // Recompute final displacements at the drop cell — same path as preview.
      const finalDisplacements = computeDisplacements(
        editable.board,
        tileId,
        x,
        y,
        CELL_WIDTH,
        CELL_HEIGHT,
      );
      if (finalDisplacements.size > 0) {
        // Build atomic move: dragged tile + all displaced neighbors.
        const moves: Array<{ tileId: string; position: { x: number; y: number } }> = [
          { tileId, position: { x, y } },
        ];
        for (const [displTileId, { dx, dy }] of finalDisplacements) {
          const entry = editable.board.layout.find((l) => l.tileId === displTileId);
          if (entry) {
            moves.push({
              tileId: displTileId,
              position: {
                x: entry.x + Math.round(dx / CELL_WIDTH),
                y: entry.y + Math.round(dy / CELL_HEIGHT),
              },
            });
          }
        }
        const commitErr = editable.commitMoveTiles(tileId, moves);
        if (commitErr === null) return;
        // Fall through to single-move on failure.
      }
      editable.commitMove(tileId, x, y);
    },
  );

  // Collision check for resize: returns true if the new size would collide.
  const checkResizeCollision = useCallback(
    (tileId: string, size: TileSize): boolean => {
      const entry = editable.board.layout.find((l) => l.tileId === tileId);
      if (!entry) return false;
      const { w, h } = sizeToWH(size);
      // Check collision, skipping the tile itself
      return hasCollision(editable.board.layout, entry.x, entry.y, w, h, tileId);
    },
    [editable.board.layout],
  );

  // Drag-to-resize (SE-corner handle) — capture FLIP rects before commit.
  // The resized tile is excluded (the outline was its live feedback).
  const resize = useDragResize(
    CELL_WIDTH,
    CELL_HEIGHT,
    checkResizeCollision,
    (tileId, size) => {
      flipCaptureRef.current?.(tileId);
      editable.commitResize(tileId, size);
    },
  );

  // FLIP animation: capture tile positions before layout mutations, replay when
  // the layout GEOMETRY signature changes (board.id is invariant across ops —
  // review fix). Data refreshes leave the signature unchanged → no animation.
  const flipLayoutSig = useMemo(
    () => layoutSignature(editable.board.layout),
    [editable.board.layout],
  );
  const flip = useFlipAnimation({
    gridRef,
    layoutSig: flipLayoutSig,
  });
  // Keep flipCaptureRef current so earlier-defined commit callbacks can call it.
  flipCaptureRef.current = flip.captureBeforeCommit;

  // Clear displacement preview when drag ends.
  // H4: also reset the hover-cell quantization guard.
  useEffect(() => {
    if (!drag.state.isDragging) {
      prevHoverCellRef.current = null;
      setDisplacements(new Map());
    }
  }, [drag.state.isDragging]);

  // H1/M4 SINGLE-OWNER TRANSFORM FIX:
  // Displacement preview transforms are applied to the INNER `.tile-displace` div
  // (nested inside [data-tile-id]), NOT to [data-tile-id] itself.
  // FLIP animation owns [data-tile-id]'s transform exclusively.
  // This prevents FLIP and displacement from clobbering each other, eliminating
  // the neighbor-jump bug on drop (H1) and the inline-transform conflict (M4).
  //
  // willChange and transition are scoped to .tile-displace — cleared on drag end.
  // On drop, displacement preview clears BEFORE FLIP plays (FLIP starts after commit
  // state update; displacement is cleared by the isDragging → false effect above).
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    // Apply transforms to the .tile-displace child of each displaced tile's cell.
    for (const [tileId, { dx, dy }] of displacements) {
      const cell = grid.querySelector<HTMLElement>(`[data-tile-id="${tileId}"]`);
      const displace = cell?.querySelector<HTMLElement>('.tile-displace');
      if (displace) {
        displace.style.transform = `translate(${dx}px, ${dy}px)`;
        displace.style.transition = `transform var(--dur-settle) var(--spring-settle)`;
        displace.style.willChange = 'transform';
        displace.classList.add('tile-preview-displaced');
      }
    }
    // Clear transforms for .tile-displace elements whose tile is NOT in displacements.
    const allCells = grid.querySelectorAll<HTMLElement>('[data-tile-id]');
    for (const cell of allCells) {
      const tileId = cell.dataset['tileId'];
      if (tileId && !displacements.has(tileId) && drag.state.dragTileId !== tileId) {
        const displace = cell.querySelector<HTMLElement>('.tile-displace');
        if (displace && displace.classList.contains('tile-preview-displaced')) {
          displace.style.transform = '';
          displace.style.transition = '';
          displace.classList.remove('tile-preview-displaced');
          displace.style.willChange = '';
        }
      }
    }
  }, [displacements, drag.state.dragTileId]);

  // Track pointer position for drag ghost rendering.
  function handlePointerMove(e: React.PointerEvent): void {
    if (editable.editMode) {
      drag.onPointerMove(e);
      resize.onPointerMove(e);
      if (drag.state.isDragging) {
        // H4: reuse cached containerRect from drag start (one getBoundingClientRect
        // per drag start, not per pointermove event).
        const rect = containerRect;
        if (rect) {
          setPointerPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }
        // Compute live push-aside displacement preview (§3.3 / SHOULD #9).
        if (drag.state.dragTileId) {
          if (!rect) return;
          const x = pointerToGridX(e.clientX - rect.left, CELL_WIDTH, editable.board.grid.cols);
          const y = pointerToGridY(e.clientY - rect.top, CELL_HEIGHT);
          // H4: quantize — skip displacement recompute if hover cell is unchanged.
          const prev = prevHoverCellRef.current;
          if (prev && prev.x === x && prev.y === y) return;
          prevHoverCellRef.current = { x, y };
          const newDisplacements = computeDisplacements(
            editable.board,
            drag.state.dragTileId,
            x,
            y,
            CELL_WIDTH,
            CELL_HEIGHT,
          );
          setDisplacements(newDisplacements);
        }
      }
    }
  }

  // getTileAllowedSizes: looks up allowed sizes from the tile type registry.
  const getTileAllowedSizes = useCallback(
    (tileId: string): readonly TileSize[] => {
      const tile = editable.board.tiles.find((t) => t.id === tileId);
      if (!tile) return [];
      return getTileTypeDef(tile.type).allowedSizes;
    },
    [editable.board.tiles],
  );

  // Keyboard nav — capture FLIP rects before keyboard-initiated commits.
  const kbNav = useKeyboardNav(
    editable.board,
    (tileId, x, y) => { flip.captureBeforeCommit(); editable.commitMove(tileId, x, y); },
    editable.editMode,
    (tileId, size) => { flip.captureBeforeCommit(); editable.commitResize(tileId, size); },
    getTileAllowedSizes,
  );

  function handleAddTile(tile: Tile): void {
    const parts = tile.display.size.split('x');
    const w = parseInt(parts[0] ?? '1', 10);
    const h = parseInt(parts[1] ?? '1', 10);
    const pos = findAddPosition(editable.board, w, h);
    editable.commitAdd(tile, pos.x, pos.y);
  }

  // Handle tile click in view mode: open detail sheet.
  const handleTileClickViewMode = useCallback((tileId: string) => {
    const tile = editable.board.tiles.find((t) => t.id === tileId) ?? null;
    setDetailTile(tile);
    // Read the live vm from the registry (populated by TileCell on each render).
    // Falls back to null — the sheet renders a fallback vm from App.tsx in that case.
    const entry = vmRegistryRef.current.get(tileId) ?? null;
    setDetailVm(entry?.vm ?? null);
    setDetailData(entry?.data ?? undefined);
  }, [editable.board.tiles]);

  return (
    <main
      style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }}
      aria-label="Your Briefpane board"
      onKeyDown={isTV ? undefined : (e) => kbNav.onKeyDown(e)}
    >
      {/* Spaces switcher: ABOVE the board so desktop tab strip sits at the top.
          Phone pill bar positions itself fixed at the bottom regardless of DOM order.
          Hidden in TV mode (isTV guard inside SpacesSwitcher). */}
      {!isTV && (
        <SpacesSwitcher
          spaces={spaces}
          activeSpaceId={activeSpaceId}
          actions={spacesActions}
          isTV={isTV}
        />
      )}

      <div
        ref={gridRef}
        id="board-panel"
        // ARIA FIX: role=tabpanel + aria-labelledby only when the desktop tab
        // strip (tablist) is actually rendered. On phone (<768px) and TV, the
        // tab ids don't exist → dangling ARIA reference.
        // Desktop (non-TV, ≥768px): tab strip present → tabpanel + labelledby.
        // Phone / TV: plain region (no role, no labelledby).
        role={!isTV && isDesktop ? 'tabpanel' : 'region'}
        aria-labelledby={!isTV && isDesktop ? `space-tab-${activeSpaceId}` : undefined}
        aria-label={!isTV && isDesktop ? undefined : 'Your Briefpane board'}
        style={{ flex: 1, position: 'relative' }}
        // TV mode: pointer events disabled at the container level — tiles are
        // display-only (spec §2.4: "Interaction: None").
        // Edit mode drag/resize handlers are also suppressed (isTV wins).
        onPointerMove={(!isTV && editable.editMode) ? handlePointerMove : undefined}
        onPointerUp={(!isTV && editable.editMode) ? (e) => { drag.onPointerUp(e); resize.onPointerUp(e); } : undefined}
        onPointerCancel={(!isTV && editable.editMode) ? () => { drag.onPointerCancel(); resize.onPointerCancel(); } : undefined}
      >
        <BoardGrid
          board={editable.board}
          cache={appCache}
          registry={appRegistry}
          // TV mode: never enter edit mode — tiles are display-only.
          editMode={!isTV && editable.editMode}
          crossfadeMap={crossfadeMap}
          isDragging={!isTV && drag.state.isDragging}
          dragTileId={!isTV ? drag.state.dragTileId : null}
          // TV mode: no edit mode entry — tiles are display-only (§6.3).
          {...(!isTV ? { onEditModeEnter: editable.toggleEditMode } : {})}
          vmRegistry={vmRegistryRef}
          // TV mode: no interaction handlers — tiles are non-interactive.
          // Default view mode: tile click opens level-1 detail sheet (core-flows §4.1).
          {...(!isTV && editable.editMode ? {
            onTileClick: (id: string) => kbNav.select(id),
            // Ghost blink fix: capture pointer position BEFORE the state flip so
            // the first render of DragGhost has a valid position (not 0,0).
            onDragStart: (id: string, e: React.PointerEvent | React.KeyboardEvent) => {
              if ('clientX' in e) {
                const rect = gridRef.current?.getBoundingClientRect() ?? null;
                if (rect) {
                  setPointerPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                  setContainerRect(rect);
                }
              }
              drag.onDragStart(id, e);
            },
            onRemove: (id: string) => { flip.captureBeforeCommit(); editable.commitRemove(id); },
            onResize: (id: string, size: TileSize) => { flip.captureBeforeCommit(); editable.commitResize(id, size); },
            onResizeStart: resize.onResizeStart,
          } : !isTV ? {
            // Default view mode: tile click opens level-1 detail sheet (core-flows §4.1).
            onTileClick: handleTileClickViewMode,
          } : {
            // TV mode: no handlers — tiles are purely decorative displays.
          })}
        />
        {/* Drag ghost: calm translucent card following the pointer (§5.1).
            Ghost is sized to the ACTUAL tile footprint (w×h cells) rather than
            one cell — matches the real tile size as specified in the deliverables.
            Also rendered during the spring-revert phase (§3.3) — phase 'reverting'
            keeps dragTileId alive while the ghost animates back to origin.
            TV mode: never in edit mode so this block is never reached. */}
        {!isTV && editable.editMode && (drag.state.phase === 'dragging' || drag.state.phase === 'reverting') && (() => {
          const dragTile = editable.board.tiles.find((t) => t.id === drag.state.dragTileId);
          const layoutEntry = editable.board.layout.find((l) => l.tileId === drag.state.dragTileId);
          return (
            <>
              <DragGhost
                dragState={drag.state}
                label={dragTile?.label ?? dragTile?.type ?? ''}
                cellWidth={CELL_WIDTH}
                cellHeight={CELL_HEIGHT}
                pointerX={pointerPos.x}
                pointerY={pointerPos.y}
                tileW={layoutEntry?.w ?? 1}
                tileH={layoutEntry?.h ?? 1}
                onRevertEnd={drag.onRevertEnd}
              />
              {/* TargetCellOutline only during active drag — not during revert. */}
              {drag.state.phase === 'dragging' && (
                <TargetCellOutline
                  dragState={drag.state}
                  containerRect={containerRect}
                  cellWidth={CELL_WIDTH}
                  cellHeight={CELL_HEIGHT}
                />
              )}
            </>
          );
        })()}
        {/* Resize outline: dashed preview footprint during drag-to-resize.
            TV mode: suppressed (never in edit mode). */}
        {!isTV && editable.editMode && resize.state.isResizing && (
          <ResizeOutline
            resizeState={resize.state}
            board={editable.board}
            containerRect={containerRect}
            cellWidth={CELL_WIDTH}
            cellHeight={CELL_HEIGHT}
          />
        )}
      </div>

      {/* Onboarding bar and edit chrome: hidden in TV mode (spec §2.4: "Chrome: None"). */}
      {!isTV && (
        <>
          {/* Onboarding bar: BELOW the board — value before configuration (core-flows §2). */}
          <OnboardingBar
            currentBoard={editable.board}
            analytics={appAnalytics}
            onBoardSwap={onBoardSwap}
            onBoardPersist={onBoardCommit}
          />

          <EditModeBar
            editMode={editable.editMode}
            onToggle={editable.toggleEditMode}
            onAddTile={() => setAddTileOpen(true)}
          />

          <AddTileDrawer
            isOpen={addTileOpen}
            onClose={() => setAddTileOpen(false)}
            onAdd={handleAddTile}
          />

          {/* Level-1 tile detail sheet (core-flows §4.1). Rendered outside the grid
              div so it overlays the entire board without z-index conflicts. */}
          <TileDetailSheet
            tile={detailTile}
            vm={detailVm ?? (detailTile ? {
              // Registry miss (tile tapped before its first commit): render an
              // honest loading state, never a blank "ok" (review M2).
              dataState: 'loading',
              glanceState: 'neutral',
              slots: { primary: '', delta: null },
              label: detailTile.label ?? null,
              fetchedAt: null,
            } : null)}
            {...(detailData ? { data: detailData } : {})}
            onClose={() => { setDetailTile(null); setDetailVm(null); setDetailData(undefined); }}
            onConfigure={editable.commitConfigure}
            analytics={appAnalytics}
          />

          {/* Discoverability affordance: calm "TV mode" link in the footer area.
              Opens ?mode=tv in a new tab (rel=noopener). One line, no nagging.
              A ?space=<name> param selects a specific space (read-only, case-insensitive). */}
          <div
            aria-label="TV mode discoverability"
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: 'var(--space-2)',
            }}
          >
            <a
              href="?mode=tv"
              target="_blank"
              rel="noopener noreferrer"
              title="Opens in TV mode. Add ?space=<name> to show a specific space."
              style={{
                fontSize: 'var(--text-timestamp)',
                color: 'var(--color-text-dim)',
                textDecoration: 'none',
                letterSpacing: '0.01em',
              }}
            >
              TV mode
            </a>
          </div>
        </>
      )}
    </main>
  );
}

export function App(): React.ReactElement {
  return <AppShell />;
}
